/**
 * useStudyConfig — single source of truth for the LIVE Prolific study
 * codes (completion + screen-out + study name). Lets the founder spin
 * up a new Prolific study and swap the codes from the admin UI without
 * a redeploy.
 *
 * Cache: localStorage for 5 minutes. After that we silently refetch on
 * the next render. New values from POST /api/admin/study-config are
 * also broadcast via a window event so other open tabs pick up the
 * change immediately.
 *
 * Failure mode: if the network request fails or returns an unexpected
 * shape, we fall back to the compile-time constants in /lib/prolific.js
 * and /lib/screener.js so the study completion flow never breaks.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { PROLIFIC_COMPLETION_CODE, PROLIFIC_COMPLETION_URL } from "@/lib/prolific";
import { SCREEN_OUT_PROLIFIC_CODE, SCREEN_OUT_PROLIFIC_URL } from "@/lib/screener";

const CACHE_KEY = "ml_study_config_v1";
const CACHE_TTL_MS = 60 * 1000; // 60 seconds — short TTL keeps the
// participant-side completion redirect honest. After founder edits a
// study config in admin, every other open tab picks it up within 60s
// instead of 5 min (the previous TTL caused the duplicate-study switch
// to ship stale codes to half a dozen participants in Feb 2026).
const UPDATE_EVENT = "ml-study-config-update";

const FALLBACK = {
    completion_code: PROLIFIC_COMPLETION_CODE,
    screen_out_code: SCREEN_OUT_PROLIFIC_CODE,
    study_name: "Memory Loss and Dementia Care Navigation",
    completion_url: PROLIFIC_COMPLETION_URL,
    screen_out_url: SCREEN_OUT_PROLIFIC_URL,
    updated_at: null,
};

function readCache() {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const { ts, value } = JSON.parse(raw);
        if (!ts || Date.now() - ts > CACHE_TTL_MS) return null;
        return value;
    } catch { return null; }
}

function writeCache(value) {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), value }));
    } catch { /* private mode — ignore */ }
}

export function broadcastStudyConfig(next) {
    if (typeof window === "undefined") return;
    writeCache(next);
    window.dispatchEvent(new CustomEvent(UPDATE_EVENT, { detail: next }));
}

export function useStudyConfig() {
    const [cfg, setCfg] = useState(() => readCache() || FALLBACK);

    const refresh = useCallback(async () => {
        try {
            const r = await api.get("/study-config");
            const next = { ...FALLBACK, ...r.data };
            writeCache(next);
            setCfg(next);
            return next;
        } catch {
            // Network blip — keep whatever we have. Constants from
            // /lib/prolific.js are the ultimate fallback.
            return null;
        }
    }, []);

    useEffect(() => {
        // ALWAYS refetch on mount (even if cached). The cached value
        // hydrates immediately so there's no flash, then the live value
        // overwrites within ~50ms. This guards against the stale-code
        // bug where a participant who returned to /study-complete days
        // after the founder swapped studies got the old completion code
        // from localStorage and Prolific rejected it.
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const handler = (e) => setCfg({ ...FALLBACK, ...(e.detail || {}) });
        window.addEventListener(UPDATE_EVENT, handler);
        return () => window.removeEventListener(UPDATE_EVENT, handler);
    }, []);

    return { ...cfg, refresh };
}
